// Considered: ChatMessageCardAtom — rejected because it is bound to `ApiMessage` (a real
// sphere-authored conversation entry: author sphere id, edited flag, HTML content sanitized via
// DOMPurify). An agent thread turn has neither an author sphere nor rich HTML — it is a plain
// role/text pair or a proposal card, so this molecule owns its own honestly-typed rendering.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import type { ComposerAgentQuestion, ComposerAgentContextConfirmation } from '../../../composer/agentTransport';
import './AgentMessageMolecule.scss';

/**
 * Contract datum ids a host whose decomposition covers this thread stamps on the turn's two
 * annotations (`type-composer.jsx:1875-1878` — the jump link and the shared-question chip). Hosts
 * outside that contract (the Context Builder, the gallery) pass nothing and emit no annotation.
 */
export interface AgentMessageParity {
  /** Container path the turn sits in, e.g. `side > panel#2 > list#2 > each(t.interview)`. */
  itemContainer: string;
  /** Datum id for the "see it" jump link. */
  jump: string;
  /** Datum id for the "asked once for both" chip. */
  shared: string;
}

export type AgentThreadItem =
  | {
      kind: 'chat';
      id: string;
      role: 'user' | 'assistant';
      content: string;
      isError?: boolean;
      /**
       * The composer section this turn is about (`m.block`) — renders the "see it" jump so the
       * user can read the change where it lives instead of re-deriving which section it hit.
       */
      block?: string;
      /**
       * This turn answered a question the agent asked once on behalf of two sections (`m.shared`)
       * — stated on the turn so the user isn't left wondering why they weren't asked twice.
       */
      shared?: boolean;
    }
  | {
      kind: 'proposal';
      id: string;
      ghostId: string;
      rationale: string;
      componentCount: number;
      status: 'pending' | 'accepted' | 'rejected';
    }
  | {
      kind: 'question';
      id: string;
      questions: ComposerAgentQuestion[];
      status: 'pending' | 'answered';
      answerSummary?: string;
    }
  | {
      kind: 'contextConfirmation';
      id: string;
      confirmation: ComposerAgentContextConfirmation;
      status: 'pending' | 'confirming' | 'confirmed' | 'rejected' | 'error';
      systemName?: string;
      /** Distinguishes the failures `POST /context/confirm` reports separately (409 / 403 / other). */
      errorCode?: ContextConfirmationErrorCode;
    };

export type ContextConfirmationErrorCode = 'nameTaken' | 'forbidden' | 'unknown';

export interface AgentMessageMoleculeProps {
  item: AgentThreadItem;
  onAccept?: (ghostId: string) => void;
  onReject?: (ghostId: string) => void;
  /** Fires once, when the user submits their selections for a pending `question` item — composes ALL questions' answers into one message. */
  onSubmitAnswers?: (itemId: string, message: string) => void;
  /** Fires when the user clicks Confirm on a pending `contextConfirmation` card (EAINT-844 Phase F6e). */
  onConfirmContext?: (itemId: string) => void;
  /** Fires when the user clicks Cancel on a pending `contextConfirmation` card. */
  onCancelContext?: (itemId: string) => void;
  /** Jumps to the section a chat turn names (`item.block`). Without it the link isn't rendered — an affordance that goes nowhere is worse than none. */
  onJumpToBlock?: (blockId: string) => void;
  /** Contract annotations for hosts whose decomposition covers this thread. */
  parity?: AgentMessageParity;
}

const OTHER_SENTINEL = '__other__';

const CONTEXT_CONFIRM_ERROR_KEYS: Record<ContextConfirmationErrorCode, string> = {
  nameTaken: 'composer.agent.contextConfirmFailedNameTaken',
  forbidden: 'composer.agent.contextConfirmFailedForbidden',
  unknown: 'composer.agent.contextConfirmFailed',
};

const CONTEXT_CONFIRM_ACTION_KEYS = {
  pending: 'composer.agent.contextConfirmConfirm',
  confirming: 'composer.agent.contextConfirming',
  error: 'composer.agent.contextConfirmRetry',
} as const;

interface QuestionSelection {
  selected: string;
  otherText: string;
}

function initialSelections(questions: ComposerAgentQuestion[]): Record<string, QuestionSelection> {
  const result: Record<string, QuestionSelection> = {};
  for (const q of questions) {
    const recommended = q.options.find((o) => o.recommended);
    result[q.id] = { selected: recommended ? recommended.label : '', otherText: '' };
  }
  return result;
}

function composeAnswerMessage(questions: ComposerAgentQuestion[], selections: Record<string, QuestionSelection>): string {
  return questions
    .map((q) => {
      const sel = selections[q.id];
      const answer = sel?.selected === OTHER_SENTINEL ? sel.otherText.trim() : sel?.selected ?? '';
      return `${q.text}\n${answer}`;
    })
    .join('\n\n');
}

function isFullyAnswered(questions: ComposerAgentQuestion[], selections: Record<string, QuestionSelection>): boolean {
  return questions.every((q) => {
    const sel = selections[q.id];
    if (!sel || !sel.selected) return false;
    if (sel.selected === OTHER_SENTINEL) return sel.otherText.trim().length > 0;
    return true;
  });
}

interface QuestionCardProps {
  itemId: string;
  questions: ComposerAgentQuestion[];
  status: 'pending' | 'answered';
  answerSummary?: string;
  onSubmitAnswers?: (itemId: string, message: string) => void;
}

/**
 * Renders `ask_structured_questions`' question set as one radio group per question (recommended
 * option pre-selected, an "Other…" free-text option when `allowOther`), with ONE submit that
 * composes every question's answer into a single next user message (EAINT-844 Phase F6b). Its own
 * component (not inlined in `AgentMessageMolecule`) so its `useState` is never called
 * conditionally after that component's early `kind === 'chat'` return (Sonar S6440).
 */
const QuestionCard: React.FC<QuestionCardProps> = ({ itemId, questions, status, answerSummary, onSubmitAnswers }) => {
  const { t } = useTranslation();
  const [selections, setSelections] = useState<Record<string, QuestionSelection>>(() => initialSelections(questions));

  if (status === 'answered') {
    return (
      <div className="agent-message agent-message--question" data-testid="agent-question-card">
        <div className="agent-message__question-answered" data-testid="agent-question-answered">
          {answerSummary}
        </div>
      </div>
    );
  }

  const updateSelected = (questionId: string, selected: string) => {
    setSelections((prev) => ({ ...prev, [questionId]: { selected, otherText: prev[questionId]?.otherText ?? '' } }));
  };
  const updateOtherText = (questionId: string, otherText: string) => {
    setSelections((prev) => ({ ...prev, [questionId]: { selected: OTHER_SENTINEL, otherText } }));
  };
  const handleSubmit = () => {
    onSubmitAnswers?.(itemId, composeAnswerMessage(questions, selections));
  };

  return (
    <div className="agent-message agent-message--question" data-testid="agent-question-card">
      {questions.map((q) => (
        <fieldset key={q.id} className="agent-message__question" data-testid={`agent-question-${q.id}`}>
          <legend className="agent-message__question-text">{q.text}</legend>
          {q.options.map((opt) => (
            <label key={opt.label} className="agent-message__question-option">
              <input
                type="radio"
                name={`agent-question-${itemId}-${q.id}`}
                value={opt.label}
                checked={selections[q.id]?.selected === opt.label}
                onChange={() => updateSelected(q.id, opt.label)}
              />
              <span className="agent-message__question-option-label">{opt.label}</span>
              {opt.recommended && (
                <span className="agent-message__question-recommended">{t('composer.agent.questionRecommended')}</span>
              )}
              {opt.description && <span className="agent-message__question-option-description">{opt.description}</span>}
            </label>
          ))}
          {q.allowOther && (
            <label className="agent-message__question-option agent-message__question-option--other">
              <input
                type="radio"
                name={`agent-question-${itemId}-${q.id}`}
                value={OTHER_SENTINEL}
                checked={selections[q.id]?.selected === OTHER_SENTINEL}
                onChange={() => updateSelected(q.id, OTHER_SENTINEL)}
              />
              <span className="agent-message__question-option-label">{t('composer.agent.questionOtherLabel')}</span>
              {selections[q.id]?.selected === OTHER_SENTINEL && (
                <input
                  type="text"
                  className="agent-message__question-other-input"
                  value={selections[q.id]?.otherText ?? ''}
                  placeholder={t('composer.agent.questionOtherPlaceholder')}
                  onChange={(e) => updateOtherText(q.id, e.target.value)}
                />
              )}
            </label>
          )}
        </fieldset>
      ))}
      <ButtonAtom variant="primary" size="small" disabled={!isFullyAnswered(questions, selections)} onClick={handleSubmit}>
        {t('composer.agent.questionSubmit')}
      </ButtonAtom>
    </div>
  );
};

/**
 * One agent-thread turn (SC8): a chat bubble, a proposal card with Accept/Reject that stays in
 * the thread history — once resolved it shows its terminal status instead of the action buttons,
 * so the log reads as a record of what happened, not a form — or a structured-questions card.
 */
const AgentMessageMolecule: React.FC<AgentMessageMoleculeProps> = ({
  item,
  onAccept,
  onReject,
  onSubmitAnswers,
  onConfirmContext,
  onCancelContext,
  onJumpToBlock,
  parity,
}) => {
  const { t } = useTranslation();

  if (item.kind === 'chat') {
    return (
      <div
        className={[
          'agent-message',
          `agent-message--${item.role}`,
          item.isError ? 'agent-message--error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={`agent-message-${item.role}`}
        data-turn-id={item.id}
        data-container={parity?.itemContainer}
      >
        {item.content}
        {item.block && onJumpToBlock && (
          <ButtonAtom
            variant="text"
            size="small"
            className="agent-message__jump"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={() => onJumpToBlock(item.block as string)}
            data-testid="agent-message-jump"
            data-datum={parity?.jump}
            data-kind="link"
            label={t('studio.typeEditor.composer.agent.seeIt')}
          />
        )}
        {item.shared && (
          <ChipAtom
            label={t('studio.typeEditor.composer.agent.askedOnceForBoth')}
            className="agent-message__shared"
            data-testid="agent-message-shared"
            data-datum={parity?.shared}
            data-kind="chip"
          />
        )}
      </div>
    );
  }

  if (item.kind === 'question') {
    return (
      <QuestionCard
        itemId={item.id}
        questions={item.questions}
        status={item.status}
        answerSummary={item.answerSummary}
        onSubmitAnswers={onSubmitAnswers}
      />
    );
  }

  if (item.kind === 'contextConfirmation') {
    return (
      <div className="agent-message agent-message--context-confirmation" data-testid="agent-context-confirmation-card">
        <div className="agent-message__context-confirmation-heading">{t('composer.agent.contextConfirmHeading')}</div>
        <div className="agent-message__context-confirmation-name">{item.confirmation.name}</div>
        {item.confirmation.description && (
          <div className="agent-message__context-confirmation-description">{item.confirmation.description}</div>
        )}
        {item.status === 'error' && (
          <output
            className="agent-message__context-confirmation-status agent-message__context-confirmation-status--error"
            data-testid="agent-context-confirmation-status-error"
          >
            {t(CONTEXT_CONFIRM_ERROR_KEYS[item.errorCode ?? 'unknown'])}
          </output>
        )}
        {/* `error` stays actionable: nothing was written, so re-confirming the same proposal is safe. */}
        {(item.status === 'pending' || item.status === 'confirming' || item.status === 'error') && (
          <div className="agent-message__context-confirmation-actions">
            <ButtonAtom
              variant="primary"
              size="small"
              disabled={item.status === 'confirming'}
              onClick={() => onConfirmContext?.(item.id)}
            >
              {t(CONTEXT_CONFIRM_ACTION_KEYS[item.status])}
            </ButtonAtom>
            <ButtonAtom variant="secondary" size="small" disabled={item.status === 'confirming'} onClick={() => onCancelContext?.(item.id)}>
              {t('composer.agent.contextConfirmCancel')}
            </ButtonAtom>
          </div>
        )}
        {item.status === 'confirmed' && (
          <output
            className="agent-message__context-confirmation-status agent-message__context-confirmation-status--confirmed"
            data-testid="agent-context-confirmation-status-confirmed"
          >
            {t('composer.agent.contextConfirmed', { systemName: item.systemName })}
          </output>
        )}
        {item.status === 'rejected' && (
          <output
            className="agent-message__context-confirmation-status agent-message__context-confirmation-status--rejected"
            data-testid="agent-context-confirmation-status-rejected"
          >
            {t('composer.agent.contextConfirmCancelled')}
          </output>
        )}
      </div>
    );
  }

  return (
    <div className="agent-message agent-message--proposal" data-testid="agent-proposal-card">
      <div className="agent-message__proposal-rationale">{item.rationale}</div>
      <div className="agent-message__proposal-count">
        {t('composer.agent.proposalComponentCount', { count: item.componentCount })}
      </div>
      {item.status === 'pending' ? (
        <div className="agent-message__proposal-actions">
          <ButtonAtom variant="primary" size="small" onClick={() => onAccept?.(item.ghostId)}>
            {t('composer.agent.proposalAccept')}
          </ButtonAtom>
          <ButtonAtom variant="secondary" size="small" onClick={() => onReject?.(item.ghostId)}>
            {t('composer.agent.proposalReject')}
          </ButtonAtom>
        </div>
      ) : (
        <div
          className={`agent-message__proposal-status agent-message__proposal-status--${item.status}`}
          data-testid={`agent-proposal-status-${item.status}`}
        >
          {t(item.status === 'accepted' ? 'composer.agent.proposalAccepted' : 'composer.agent.proposalRejected')}
        </div>
      )}
    </div>
  );
};

export default AgentMessageMolecule;
